import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/api';
import './ShopPerformance.css';
import './modern-date-range.css';
import GradientHeading from './GradientHeading';
import Loader from './Loader';

const PAGE_SIZE = 10;

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatPercent = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return value.toFixed(2) + '%';
};


const dateOptions = [
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last Week', value: 'last_week' },
  { label: 'Last 30 Days', value: 'last_30' },
  { label: 'Custom Range', value: 'custom' }
];

const getYesterday = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return yesterday;
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getRange = (option, customStart, customEnd) => {
  const yesterday = getYesterday();
  if (option === 'yesterday') {
    return { start: formatDate(yesterday), end: formatDate(yesterday) };
  }
  if (option === 'last_week') {
    const end = new Date(yesterday);
    const start = new Date(yesterday);
    start.setDate(end.getDate() - 6);
    return { start: formatDate(start), end: formatDate(end) };
  }
  if (option === 'last_30') {
    const end = new Date(yesterday);
    const start = new Date(yesterday);
    start.setDate(end.getDate() - 29);
    return { start: formatDate(start), end: formatDate(end) };
  }
  if (option === 'custom') {
    if (!customStart || !customEnd) return { start: '', end: '' };
    // Clamp end date to yesterday if needed
    const endDate = new Date(customEnd);
    const yest = getYesterday();
    const safeEnd = endDate > yest ? yest : endDate;
    return { start: formatDate(new Date(customStart)), end: formatDate(safeEnd) };
  }
  return { start: '', end: '' };
};

const ShopPerformance = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dateOption, setDateOption] = useState('yesterday');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const { start, end } = getRange(dateOption, customStart, customEnd);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          page,
          page_size: PAGE_SIZE,
          start_date: start,
          end_date: end
        };
        const res = await apiClient.get('/products_performance', { params });
        setProducts(res.data.products || []);
        setTotalCount(res.data.total_count || 0);
      } catch (err) {
        setError('Failed to load products.');
      } finally {
        setLoading(false);
      }
    };
    if (start && end) fetchProducts();
  }, [page, start, end]);

  useEffect(() => {

  }, []);

  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => (p * PAGE_SIZE < totalCount ? p + 1 : p));
  
  const handleRowClick = (productId) => {
    setSelectedRow(selectedRow === productId ? null : productId);
  };

  return (
    <div className="page-relative-wrapper">
      {loading && (
        <div className="page-overlay-loader">
          <Loader fullPage={false} text="Syncing Product Data..." />
        </div>
      )}
      <div className={`shop-performance-container ${loading ? 'content-blur' : ''}`}>
      <div className="shop-performance-header">
        <GradientHeading>Products Performance</GradientHeading>
        <div className="date-range-card-modern">
          <div className="date-range-label">
            {/* <span role="img" aria-label="calendar" className="date-range-icon"></span> */}
            <span className="date-range-title">Date Range:</span>
          </div>
          <select
            id="dateOption"
            value={dateOption}
            onChange={e => setDateOption(e.target.value)}
            className="modern-select"
          >
            {dateOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {dateOption === 'custom' && (
            <div className="custom-date-range-modern">
              <input
                type="date"
                value={customStart}
                max={formatDate(getYesterday())}
                onChange={e => setCustomStart(e.target.value)}
                className="modern-date-input"
              />
              <span className="to-label">to</span>
              <input
                type="date"
                value={customEnd}
                max={formatDate(getYesterday())}
                onChange={e => setCustomEnd(e.target.value)}
                className="modern-date-input"
              />
            </div>
          )}
        </div>
        <div className="performance-summary">
          <div className="summary-card">
            <span className="summary-value">{totalCount}</span>
            <span className="summary-label">Total Products</span>
          </div>
          <div className="summary-card">
            <span className="summary-value">{formatCurrency(products.reduce((sum, p) => sum + (p.gmv || 0), 0))}</span>
            <span className="summary-label">Total GMV</span>
          </div>
          <div className="summary-card">
            <span className="summary-value">{products.reduce((sum, p) => sum + (p.units_sold || 0), 0)}</span>
            <span className="summary-label">Units Sold</span>
          </div>
        </div>
      </div>

      <div className="shop-performance-content">
        <div className="table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Product Name</th>
                <th>Status</th>
                <th>Units Sold</th>
                <th>GMV</th>
                <th>Orders</th>
                <th>Product Views</th>
                <th>Click Through Rate</th>
                <th>Refunds</th>
                <th>Avg. Price</th>
                <th>Conversion Rate</th>
              </tr>
            </thead>
            {error ? (
              <tbody>
                <tr><td colSpan={11} className="error"><i className="error-icon">⚠️</i> {error}</td></tr>
              </tbody>
            ) : (
              <tbody>
                {products.length === 0 ? (
                  <tr><td colSpan={11} className="no-data">No products found.</td></tr>
                ) : (
                  products.map((prod) => (
                    <tr 
                      key={prod.product_id} 
                      className={selectedRow === prod.product_id ? 'selected-row' : ''}
                      onClick={() => handleRowClick(prod.product_id)}
                    >
                      <td>
                        <div className="product-image-container">
                          <img
                            src={prod.image_url || ''}
                            alt={prod.product_name}
                            className="product-img"
                            onError={e => { e.target.onerror = null; e.target.src = '/no-image.png'; }}
                          />
                        </div>
                      </td>
                      <td className="product-name">{prod.product_name}</td>
                      <td>
                        <span className={`status-badge status-${prod.status?.toLowerCase()}`}>
                          {prod.status}
                        </span>
                      </td>
                      <td className="numeric">{prod.units_sold ?? '-'}</td>
                      <td className="numeric highlight">{formatCurrency(prod.gmv)}</td>
                      <td className="numeric">{prod.orders ?? '-'}</td>
                      <td className="numeric">{prod.page_views ?? '-'}</td>
                      <td className="numeric">{formatPercent(prod.click_through_rate)}</td>
                      <td className="numeric">{formatCurrency(prod.refunds)}</td>
                      <td className="numeric">{formatCurrency(prod.avg_price)}</td>
                      <td className="numeric">{formatPercent(prod.conversion_rate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            )}
          </table>
        </div>

        <div className="pagination-container">
          <button className="pagination-button" onClick={handlePrev} disabled={page === 1}>
            <span className="pagination-arrow">←</span> Previous
          </button>
          <div className="pagination-info">
            Page <span className="page-number">{page}</span> of <span className="page-number">{Math.ceil(totalCount / PAGE_SIZE) || 1}</span>
          </div>
          <button className="pagination-button" onClick={handleNext} disabled={page * PAGE_SIZE >= totalCount}>
            Next <span className="pagination-arrow">→</span>
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ShopPerformance;